/**
 * UI Store - Zustand state management for UI state
 * Handles sidebar state for desktop and mobile views
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface UIState {
  isSidebarCollapsed: boolean; // Desktop collapsed state
  isMobileSidebarOpen: boolean; // Mobile overlay state
  collapsedSections: Record<string, boolean>; // Section title -> collapsed?
}

interface UIActions {
  toggleSidebar: () => void; // Toggle desktop collapsed
  toggleMobileSidebar: () => void; // Toggle mobile open
  closeMobileSidebar: () => void; // Close mobile sidebar
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSection: (title: string) => void; // Toggle a nav section's collapsed state
}

type UIStore = UIState & UIActions;

const initialState: UIState = {
  isSidebarCollapsed: false,
  isMobileSidebarOpen: false,
  collapsedSections: {},
};

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      ...initialState,

      toggleSidebar: () => {
        set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed }));
      },

      toggleMobileSidebar: () => {
        set((state) => ({ isMobileSidebarOpen: !state.isMobileSidebarOpen }));
      },

      closeMobileSidebar: () => {
        set({ isMobileSidebarOpen: false });
      },

      setSidebarCollapsed: (collapsed) => {
        set({ isSidebarCollapsed: collapsed });
      },

      toggleSection: (title) => {
        set((state) => ({
          collapsedSections: {
            ...state.collapsedSections,
            [title]: !state.collapsedSections[title],
          },
        }));
      },
    }),
    {
      name: 'ui-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist section-collapse state; sidebar collapse/mobile-open should
      // reset to defaults each session, not be carried across reloads.
      partialize: (state) => ({ collapsedSections: state.collapsedSections }),
    },
  ),
);
